import { motion } from 'framer-motion';
import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { fontWeights } from '@/Domain/utils/font-weight';
import { useShape } from '@/Domain/utils/shape-context';
import { springs } from '@/Domain/utils/springs';
import { cn } from '@/Domain/utils/utils';

const COLOR_FORMATS = ['hex', 'rgb', 'hsl', 'oklch'] as const;

type ColorFormat = (typeof COLOR_FORMATS)[number];

interface ParsedColor {
  h: number;
  s: number;
  v: number;
  a: number;
  r: number;
  g: number;
  b: number;
  hex: string;
  rgb: string;
  hsl: string;
  oklch: string;
}

interface ColorPickerProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange' | 'defaultValue'
> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string, parsed: ParsedColor) => void;
  format?: ColorFormat;
  defaultFormat?: ColorFormat;
  onFormatChange?: (format: ColorFormat) => void;
  /** Formats offered in the format select. Narrow this when the consuming API
   *  accepts a subset, so an out-of-contract value cannot be produced at all. */
  allowedFormats?: readonly ColorFormat[];
  swatches?: string[];
  hideEyedropper?: boolean;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const clamp255 = (n: number) => Math.max(0, Math.min(255, n));

function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const c = v * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 1) [r, g, b] = [c, x, 0];
  else if (hh < 2) [r, g, b] = [x, c, 0];
  else if (hh < 3) [r, g, b] = [0, c, x];
  else if (hh < 4) [r, g, b] = [0, x, c];
  else if (hh < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = v - c;
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const v = max;
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, v };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d > 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 1) [r, g] = [c, x];
  else if (hh < 2) [r, g] = [x, c];
  else if (hh < 3) [g, b] = [c, x];
  else if (hh < 4) [g, b] = [x, c];
  else if (hh < 5) [r, b] = [x, c];
  else [r, b] = [c, x];
  const m = l - c / 2;
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

function srgbToLinear(c: number): number {
  c = c / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return clamp01(v) * 255;
}

function linearRgbToOklab(r: number, g: number, b: number): { L: number; a: number; b: number } {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const lq = Math.cbrt(l);
  const mq = Math.cbrt(m);
  const sq = Math.cbrt(s);
  return {
    L: 0.2104542553 * lq + 0.793617785 * mq - 0.0040720468 * sq,
    a: 1.9779984951 * lq - 2.428592205 * mq + 0.4505937099 * sq,
    b: 0.0259040371 * lq + 0.7827717662 * mq - 0.808675766 * sq,
  };
}

function oklabToLinearRgb(L: number, a: number, b: number): { r: number; g: number; b: number } {
  const lq = L + 0.3963377774 * a + 0.2158037573 * b;
  const mq = L - 0.1055613458 * a - 0.0638541728 * b;
  const sq = L - 0.0894841775 * a - 1.291485548 * b;
  const l = lq * lq * lq;
  const m = mq * mq * mq;
  const s = sq * sq * sq;
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

function rgbToOklch(r: number, g: number, b: number): { L: number; C: number; H: number } {
  const lab = linearRgbToOklab(srgbToLinear(r), srgbToLinear(g), srgbToLinear(b));
  const C = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  let H = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { L: lab.L, C, H };
}

function oklchToRgb(L: number, C: number, H: number): { r: number; g: number; b: number } {
  const a = C * Math.cos((H * Math.PI) / 180);
  const b = C * Math.sin((H * Math.PI) / 180);
  const lin = oklabToLinearRgb(L, a, b);
  return {
    r: clamp255(linearToSrgb(lin.r)),
    g: clamp255(linearToSrgb(lin.g)),
    b: clamp255(linearToSrgb(lin.b)),
  };
}

const to2hex = (n: number) => Math.round(clamp255(n)).toString(16).padStart(2, '0');

function rgbToHexStr(r: number, g: number, b: number, a: number): string {
  if (a >= 1) return `#${to2hex(r)}${to2hex(g)}${to2hex(b)}`;
  return `#${to2hex(r)}${to2hex(g)}${to2hex(b)}${to2hex(a * 255)}`;
}

function expandShortHex(h: string): string {
  if (h.length === 3 || h.length === 4)
    return h
      .split('')
      .map((c) => c + c)
      .join('');
  return h;
}

function parseHex(input: string): { r: number; g: number; b: number; a: number } | null {
  const m = input.trim().match(/^#?([0-9a-fA-F]{3,8})$/);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3 || h.length === 4) h = expandShortHex(h);
  if (h.length === 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: 1,
    };
  }
  if (h.length === 8) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: parseInt(h.slice(6, 8), 16) / 255,
    };
  }
  return null;
}

function parseColor(input: string): { r: number; g: number; b: number; a: number } | null {
  const s = input.trim();
  if (!s) return null;
  if (s.startsWith('#') || /^[0-9a-fA-F]{3,8}$/.test(s)) return parseHex(s);
  const rgbM = s.match(/^rgba?\(\s*([^)]+)\)$/i);
  if (rgbM) {
    const parts = rgbM[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const r = parseFloat(parts[0]);
    const g = parseFloat(parts[1]);
    const b = parseFloat(parts[2]);
    let a = 1;
    if (parts[3] !== undefined) {
      a = parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
    }
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return { r: clamp255(r), g: clamp255(g), b: clamp255(b), a: clamp01(a) };
  }
  const hslM = s.match(/^hsla?\(\s*([^)]+)\)$/i);
  if (hslM) {
    const parts = hslM[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const h = parseFloat(parts[0]);
    const sat = parts[1].endsWith('%') ? parseFloat(parts[1]) / 100 : parseFloat(parts[1]);
    const l = parts[2].endsWith('%') ? parseFloat(parts[2]) / 100 : parseFloat(parts[2]);
    let a = 1;
    if (parts[3] !== undefined) {
      a = parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
    }
    if ([h, sat, l, a].some(Number.isNaN)) return null;
    const rgb = hslToRgb(h, clamp01(sat), clamp01(l));
    return { r: clamp255(rgb.r), g: clamp255(rgb.g), b: clamp255(rgb.b), a: clamp01(a) };
  }
  const oklchM = s.match(/^oklch\(\s*([^)]+)\)$/i);
  if (oklchM) {
    const parts = oklchM[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const L = parts[0].endsWith('%') ? parseFloat(parts[0]) / 100 : parseFloat(parts[0]);
    const C = parseFloat(parts[1]);
    const H = parseFloat(parts[2]);
    let a = 1;
    if (parts[3] !== undefined) {
      a = parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
    }
    if ([L, C, H, a].some(Number.isNaN)) return null;
    const rgb = oklchToRgb(clamp01(L), Math.max(0, C), H);
    return { r: clamp255(rgb.r), g: clamp255(rgb.g), b: clamp255(rgb.b), a: clamp01(a) };
  }
  return null;
}

function buildParsed(h: number, s: number, v: number, a: number): ParsedColor {
  const { r, g, b } = hsvToRgb(h, s, v);
  const hsl = rgbToHsl(r, g, b);
  const oklch = rgbToOklch(r, g, b);
  const hex = rgbToHexStr(r, g, b, a);
  const rgbStr =
    a >= 1
      ? `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`
      : `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Number(a.toFixed(3))})`;
  const hslStr =
    a >= 1
      ? `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s * 100)}%, ${Math.round(hsl.l * 100)}%)`
      : `hsla(${Math.round(hsl.h)}, ${Math.round(hsl.s * 100)}%, ${Math.round(hsl.l * 100)}%, ${Number(a.toFixed(3))})`;
  const oklchStr =
    a >= 1
      ? `oklch(${(oklch.L * 100).toFixed(1)}% ${oklch.C.toFixed(3)} ${oklch.H.toFixed(1)})`
      : `oklch(${(oklch.L * 100).toFixed(1)}% ${oklch.C.toFixed(3)} ${oklch.H.toFixed(1)} / ${Number(a.toFixed(3))})`;
  return {
    h,
    s,
    v,
    a,
    r: Math.round(r),
    g: Math.round(g),
    b: Math.round(b),
    hex,
    rgb: rgbStr,
    hsl: hslStr,
    oklch: oklchStr,
  };
}

function formatValueByFormat(parsed: ParsedColor, fmt: ColorFormat): string {
  switch (fmt) {
    case 'hex':
      return parsed.hex;
    case 'rgb':
      return parsed.rgb;
    case 'hsl':
      return parsed.hsl;
    case 'oklch':
      return parsed.oklch;
  }
}

const PANEL_WIDTH = 280;
const SQUARE_HEIGHT = 156;
const BAR_HEIGHT = 12;
const BAR_THUMB_SIZE = 16;
const CHECKER_BG: CSSProperties = {
  backgroundImage: 'conic-gradient(#bbb 0 25%, #fff 0 50%, #bbb 0 75%, #fff 0)',
  backgroundSize: '8px 8px',
};

function SaturationSquare({
  h,
  s,
  v,
  onChange,
}: {
  h: number;
  s: number;
  v: number;
  onChange: (s: number, v: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [focused, setFocused] = useState(false);

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const x = clamp01((clientX - rect.left) / rect.width);
      const y = clamp01((clientY - rect.top) / rect.height);
      onChange(x, 1 - y);
    },
    [onChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      dragging.current = true;
      const el = e.currentTarget as HTMLElement;
      if (typeof el.setPointerCapture === 'function') {
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      updateFromPointer(e.clientX, e.clientY);
    },
    [updateFromPointer],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      updateFromPointer(e.clientX, e.clientY);
    },
    [updateFromPointer],
  );

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 0.1 : 0.01;
      let nextS = s;
      let nextV = v;
      let handled = true;
      if (e.key === 'ArrowLeft') nextS = clamp01(s - step);
      else if (e.key === 'ArrowRight') nextS = clamp01(s + step);
      else if (e.key === 'ArrowUp') nextV = clamp01(v + step);
      else if (e.key === 'ArrowDown') nextV = clamp01(v - step);
      else handled = false;
      if (handled) {
        e.preventDefault();
        onChange(nextS, nextV);
      }
    },
    [onChange, s, v],
  );

  const { r, g, b } = hsvToRgb(h, s, v);
  const thumbColor = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;

  return (
    <div
      ref={ref}
      role="application"
      aria-label="Saturation and brightness"
      tabIndex={0}
      onFocus={(e) => {
        if (e.currentTarget.matches(':focus-visible')) setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      className="relative w-full select-none touch-none cursor-crosshair rounded-lg overflow-hidden outline-none"
      style={{
        height: SQUARE_HEIGHT,
        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${h}, 100%, 50%))`,
        boxShadow: focused ? '0 0 0 2px var(--ring)' : undefined,
      }}
    >
      <motion.div
        className="absolute pointer-events-none rounded-full"
        initial={false}
        animate={{ left: `${s * 100}%`, top: `${(1 - v) * 100}%` }}
        transition={dragging.current ? { duration: 0 } : springs.fast}
        style={{
          transform: 'translate(-50%, -50%)',
          width: 14,
          height: 14,
          border: '2px solid white',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
          backgroundColor: thumbColor,
        }}
      />
    </div>
  );
}

function ChannelBar({
  value,
  min,
  max,
  step,
  onChange,
  trackStyle,
  thumbColor,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  trackStyle: CSSProperties;
  thumbColor: string;
  ariaLabel: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [focused, setFocused] = useState(false);

  const updateFromPointer = useCallback(
    (clientX: number) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const ratio = clamp01((clientX - rect.left) / rect.width);
      const raw = min + ratio * (max - min);
      const snapped = Math.round((raw - min) / step) * step + min;
      onChange(Math.max(min, Math.min(max, snapped)));
    },
    [min, max, step, onChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      dragging.current = true;
      const el = e.currentTarget as HTMLElement;
      if (typeof el.setPointerCapture === 'function') {
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      updateFromPointer(e.clientX);
    },
    [updateFromPointer],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      updateFromPointer(e.clientX);
    },
    [updateFromPointer],
  );

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const stepSize = e.shiftKey ? step * 10 : step;
      let next = value;
      let handled = true;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(min, value - stepSize);
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp')
        next = Math.min(max, value + stepSize);
      else if (e.key === 'Home') next = min;
      else if (e.key === 'End') next = max;
      else handled = false;
      if (handled) {
        e.preventDefault();
        onChange(next);
      }
    },
    [min, max, step, value, onChange],
  );

  const ratio = max === min ? 0 : (value - min) / (max - min);

  return (
    <div
      ref={ref}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      tabIndex={0}
      onFocus={(e) => {
        if (e.currentTarget.matches(':focus-visible')) setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      className="relative w-full cursor-ew-resize touch-none select-none outline-none rounded-full"
      style={{
        height: BAR_HEIGHT,
        boxShadow: focused ? '0 0 0 2px var(--ring)' : undefined,
        ...trackStyle,
      }}
    >
      <motion.span
        className="absolute pointer-events-none rounded-full"
        initial={false}
        animate={{ left: `${ratio * 100}%` }}
        transition={dragging.current ? { duration: 0 } : springs.fast}
        style={{
          width: BAR_THUMB_SIZE,
          height: BAR_THUMB_SIZE,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          border: '2px solid white',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
          backgroundColor: thumbColor,
        }}
      />
    </div>
  );
}

const FORMAT_LABELS: Record<ColorFormat, string> = {
  hex: 'HEX',
  rgb: 'RGB',
  hsl: 'HSL',
  oklch: 'OKLCH',
};

function FormatSelect({
  value,
  options,
  onChange,
}: {
  value: ColorFormat;
  options: readonly ColorFormat[];
  onChange: (f: ColorFormat) => void;
}) {
  if (options.length < 2) return null;

  return (
    <Select value={value} onValueChange={(v) => onChange(v as ColorFormat)}>
      <SelectTrigger className="h-9 text-[13px]" data-testid="color-picker-format">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((fmt) => (
          <SelectItem key={fmt} value={fmt} className="text-[13px]">
            {FORMAT_LABELS[fmt]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ColorInputProps {
  value: string;
  onCommit: (next: string) => void;
  ariaLabel: string;
  align?: 'left' | 'center' | 'right';
  prefix?: ReactNode;
  inputMode?: 'numeric' | 'decimal' | 'text';
  nudgeStep?: number;
  nudgeShiftStep?: number;
  hasPercent?: boolean;
  decimals?: number;
}

function ColorInput({
  value,
  onCommit,
  ariaLabel,
  align = 'left',
  prefix,
  inputMode = 'text',
  nudgeStep,
  nudgeShiftStep,
  hasPercent = false,
  decimals,
}: ColorInputProps) {
  const [draft, setDraft] = useState(value);
  const focusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrubState = useRef<{ clientX: number; startValue: number; pointerId: number } | null>(
    null,
  );
  const didScrub = useRef(false);
  const shape = useShape();
  const isScrubbable = nudgeStep != null;

  useEffect(() => {
    if (!focusedRef.current) setDraft(value);
  }, [value]);

  const formatNumber = (n: number) => {
    const formatted = decimals != null ? n.toFixed(decimals) : String(Math.round(n));
    return hasPercent ? `${formatted}%` : formatted;
  };

  const nudge = (direction: 1 | -1, shift: boolean) => {
    const baseStep = shift ? (nudgeShiftStep ?? 10) : (nudgeStep ?? 1);
    const cur = parseFloat(draft.replace('%', ''));
    if (Number.isNaN(cur)) return;
    const next = cur + direction * baseStep;
    const withSuffix = formatNumber(next);
    setDraft(withSuffix);
    onCommit(withSuffix);
  };

  const handleScrubPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isScrubbable) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const cur = parseFloat(draft.replace('%', ''));
    if (Number.isNaN(cur)) return;
    scrubState.current = { clientX: e.clientX, startValue: cur, pointerId: e.pointerId };
    didScrub.current = false;
    const el = e.currentTarget as HTMLElement;
    if (typeof el.setPointerCapture === 'function') {
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  };

  const handleScrubPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubState.current) return;
    const dx = e.clientX - scrubState.current.clientX;
    if (!didScrub.current) {
      if (Math.abs(dx) < 3) return;
      didScrub.current = true;
      if (
        document.activeElement instanceof HTMLElement &&
        document.activeElement === inputRef.current
      ) {
        inputRef.current?.blur();
      }
    }
    e.preventDefault();
    const stepSize = e.shiftKey ? (nudgeShiftStep ?? 10) : (nudgeStep ?? 1);
    const next = scrubState.current.startValue + dx * stepSize;
    const withSuffix = formatNumber(next);
    setDraft(withSuffix);
    onCommit(withSuffix);
  };

  const handleScrubPointerUp = () => {
    scrubState.current = null;
  };

  return (
    <div
      className={cn(
        'flex items-center h-9 px-2 border border-border bg-transparent transition-colors duration-75 focus-within:ring-2 focus-within:ring-ring/40 min-w-0',
        shape.input,
        isScrubbable && 'cursor-ew-resize select-none',
      )}
      onPointerDown={handleScrubPointerDown}
      onPointerMove={handleScrubPointerMove}
      onPointerUp={handleScrubPointerUp}
      onPointerCancel={handleScrubPointerUp}
      onClickCapture={(e) => {
        if (didScrub.current) {
          e.preventDefault();
          e.stopPropagation();
          didScrub.current = false;
        }
      }}
    >
      {prefix && (
        <span className="text-[12px] text-muted-foreground mr-1 select-none">{prefix}</span>
      )}
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => {
          focusedRef.current = true;
          e.currentTarget.select();
        }}
        onBlur={() => {
          focusedRef.current = false;
          if (draft !== value) onCommit(draft);
          else setDraft(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
          else if (e.key === 'Escape') {
            setDraft(value);
            (e.currentTarget as HTMLInputElement).blur();
          } else if (
            (nudgeStep != null || nudgeShiftStep != null) &&
            (e.key === 'ArrowUp' || e.key === 'ArrowDown')
          ) {
            e.preventDefault();
            nudge(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey);
          }
        }}
        inputMode={inputMode}
        aria-label={ariaLabel}
        className={cn(
          'flex-1 min-w-0 bg-transparent text-foreground text-[13px] outline-none tabular-nums',
          align === 'center' && 'text-center',
          align === 'right' && 'text-right',
          isScrubbable && 'cursor-ew-resize focus:cursor-text',
        )}
        style={{ fontVariationSettings: fontWeights.medium }}
      />
    </div>
  );
}

interface EyeDropperGlobal {
  open(): Promise<{ sRGBHex: string }>;
}

function EyeDropperButton({ onPick }: { onPick: (hex: string) => void }) {
  const [supported, setSupported] = useState(false);
  const shape = useShape();

  useEffect(() => {
    setSupported(typeof window !== 'undefined' && 'EyeDropper' in window);
  }, []);

  if (!supported) return null;

  const handleClick = async () => {
    try {
      const Ctor = (window as unknown as { EyeDropper: new () => EyeDropperGlobal }).EyeDropper;
      const eye = new Ctor();
      const result = await eye.open();
      onPick(result.sRGBHex);
    } catch {
      /* user cancelled */
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Pick color from screen"
      className={cn(
        'flex items-center justify-center w-full h-9 px-3 text-foreground border border-border bg-transparent hover:bg-accent transition-colors duration-75 outline-none focus-visible:ring-2 focus-visible:ring-ring/40 cursor-pointer',
        shape.input,
      )}
    >
      <svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m2 22 1-1h3l9-9" />
        <path d="M3 21v-3l9-9" />
        <path d="m15 6 3.4-3.4a2.121 2.121 0 1 1 3 3L18 9l.4.4a2.121 2.121 0 1 1-3 3l-3.8-3.8a2.121 2.121 0 1 1 3-3L15 6Z" />
      </svg>
    </button>
  );
}

function ColorSwatch({
  color,
  size = 24,
  selected,
  onClick,
}: {
  color: string;
  size?: number;
  selected?: boolean;
  onClick: () => void;
}) {
  const shape = useShape();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Select color ${color}`}
      className={cn(
        'relative shrink-0 overflow-hidden cursor-pointer outline-none transition-shadow duration-100',
        shape.bg,
      )}
      style={{
        width: size,
        height: size,
        ...CHECKER_BG,
        boxShadow: selected
          ? 'inset 0 0 0 1px rgba(127,127,127,0.25), 0 0 0 2px var(--background), 0 0 0 4px var(--ring)'
          : 'inset 0 0 0 1px rgba(127,127,127,0.25)',
      }}
    >
      <span className="absolute inset-0" style={{ background: color }} />
    </button>
  );
}

function SwatchStrip({
  swatches,
  current,
  onPick,
}: {
  swatches: string[];
  current: string;
  onPick: (color: string) => void;
}) {
  const normalizedCurrent = useMemo(() => {
    const p = parseColor(current);
    return p ? rgbToHexStr(p.r, p.g, p.b, p.a).toLowerCase() : '';
  }, [current]);

  return (
    <div className="flex flex-wrap gap-1.5">
      {swatches.map((sw, i) => {
        const parsed = parseColor(sw);
        const normalized = parsed
          ? rgbToHexStr(parsed.r, parsed.g, parsed.b, parsed.a).toLowerCase()
          : sw.toLowerCase();
        return (
          <ColorSwatch
            key={`${sw}-${i}`}
            color={sw}
            selected={normalized === normalizedCurrent}
            onClick={() => onPick(sw)}
          />
        );
      })}
    </div>
  );
}

type ChannelKey =
  | 'hex'
  | 'r'
  | 'g'
  | 'b'
  | 'hSL'
  | 'sSL'
  | 'lSL'
  | 'L'
  | 'C'
  | 'H'
  | 'alphaPercent';

function AlphaInput({ value, onCommit }: { value: number; onCommit: (n: number) => void }) {
  return (
    <ColorInput
      value={`${value}%`}
      onCommit={(input) => {
        const n = parseFloat(input.replace('%', ''));
        if (Number.isNaN(n)) return;
        onCommit(Math.max(0, Math.min(100, Math.round(n))));
      }}
      ariaLabel="Alpha"
      align="center"
      inputMode="numeric"
      nudgeStep={1}
      nudgeShiftStep={10}
      hasPercent
    />
  );
}

function ColorInputsRow({
  parsed,
  format,
  onChannelChange,
}: {
  parsed: ParsedColor;
  format: ColorFormat;
  onChannelChange: (key: ChannelKey, value: string) => void;
}) {
  const alphaPct = Math.round(parsed.a * 100);

  if (format === 'hex') {
    const hexNoHash = parsed.hex.replace(/^#/, '').toUpperCase();
    return (
      <div className="grid grid-cols-2 gap-2">
        <ColorInput
          value={hexNoHash}
          onCommit={(next) => onChannelChange('hex', next.startsWith('#') ? next : `#${next}`)}
          ariaLabel="Hex value"
          prefix="#"
        />
        <AlphaInput value={alphaPct} onCommit={(n) => onChannelChange('alphaPercent', String(n))} />
      </div>
    );
  }

  if (format === 'rgb') {
    return (
      <div className="grid grid-cols-4 gap-2">
        <ColorInput
          value={String(parsed.r)}
          onCommit={(n) => onChannelChange('r', n)}
          ariaLabel="Red"
          align="center"
          inputMode="numeric"
          nudgeStep={1}
          nudgeShiftStep={10}
        />
        <ColorInput
          value={String(parsed.g)}
          onCommit={(n) => onChannelChange('g', n)}
          ariaLabel="Green"
          align="center"
          inputMode="numeric"
          nudgeStep={1}
          nudgeShiftStep={10}
        />
        <ColorInput
          value={String(parsed.b)}
          onCommit={(n) => onChannelChange('b', n)}
          ariaLabel="Blue"
          align="center"
          inputMode="numeric"
          nudgeStep={1}
          nudgeShiftStep={10}
        />
        <AlphaInput value={alphaPct} onCommit={(n) => onChannelChange('alphaPercent', String(n))} />
      </div>
    );
  }

  if (format === 'hsl') {
    const hsl = rgbToHsl(parsed.r, parsed.g, parsed.b);
    return (
      <div className="grid grid-cols-4 gap-2">
        <ColorInput
          value={String(Math.round(hsl.h))}
          onCommit={(n) => onChannelChange('hSL', n)}
          ariaLabel="Hue"
          align="center"
          inputMode="numeric"
          nudgeStep={1}
          nudgeShiftStep={10}
        />
        <ColorInput
          value={String(Math.round(hsl.s * 100))}
          onCommit={(n) => onChannelChange('sSL', n)}
          ariaLabel="Saturation"
          align="center"
          inputMode="numeric"
          nudgeStep={1}
          nudgeShiftStep={10}
        />
        <ColorInput
          value={String(Math.round(hsl.l * 100))}
          onCommit={(n) => onChannelChange('lSL', n)}
          ariaLabel="Lightness"
          align="center"
          inputMode="numeric"
          nudgeStep={1}
          nudgeShiftStep={10}
        />
        <AlphaInput value={alphaPct} onCommit={(n) => onChannelChange('alphaPercent', String(n))} />
      </div>
    );
  }

  const oklch = rgbToOklch(parsed.r, parsed.g, parsed.b);
  return (
    <div className="grid grid-cols-4 gap-1">
      <ColorInput
        value={(oklch.L * 100).toFixed(0)}
        onCommit={(n) => onChannelChange('L', n)}
        ariaLabel="Lightness"
        align="center"
        inputMode="decimal"
        nudgeStep={1}
        nudgeShiftStep={10}
      />
      <ColorInput
        value={oklch.C.toFixed(2)}
        onCommit={(n) => onChannelChange('C', n)}
        ariaLabel="Chroma"
        align="center"
        inputMode="decimal"
        nudgeStep={0.01}
        nudgeShiftStep={0.1}
        decimals={2}
      />
      <ColorInput
        value={oklch.H.toFixed(0)}
        onCommit={(n) => onChannelChange('H', n)}
        ariaLabel="Hue"
        align="center"
        inputMode="numeric"
        nudgeStep={1}
        nudgeShiftStep={10}
      />
      <AlphaInput value={alphaPct} onCommit={(n) => onChannelChange('alphaPercent', String(n))} />
    </div>
  );
}

const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  (
    {
      value,
      defaultValue = '#000000',
      onValueChange,
      format,
      defaultFormat = 'hex',
      onFormatChange,
      allowedFormats = COLOR_FORMATS,
      swatches,
      hideEyedropper,
      className,
      ...props
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(value ?? defaultValue);
    const currentRawValue = isControlled ? (value as string) : internalValue;

    const isFormatControlled = format !== undefined;
    const [internalFormat, setInternalFormat] = useState<ColorFormat>(defaultFormat);
    const requestedFormat = isFormatControlled ? (format as ColorFormat) : internalFormat;
    const currentFormat = allowedFormats.includes(requestedFormat)
      ? requestedFormat
      : (allowedFormats[0] ?? 'hex');

    const initialParsed = useMemo(() => {
      const p = parseColor(currentRawValue);
      if (!p) return { h: 0, s: 1, v: 1, a: 1 };
      const hsv = rgbToHsv(p.r, p.g, p.b);
      return { h: hsv.s === 0 ? 0 : hsv.h, s: hsv.s, v: hsv.v, a: p.a };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [hsv, setHsv] = useState(initialParsed);

    const lastEmittedRef = useRef<string>('');
    useEffect(() => {
      if (!isControlled) return;
      const cur = value as string;
      if (cur === lastEmittedRef.current) return;
      const p = parseColor(cur);
      if (!p) return;
      const newHsv = rgbToHsv(p.r, p.g, p.b);
      setHsv((prev) => ({
        h: newHsv.s === 0 ? prev.h : newHsv.h,
        s: newHsv.s,
        v: newHsv.v,
        a: p.a,
      }));
    }, [value, isControlled]);

    const parsed = useMemo(() => buildParsed(hsv.h, hsv.s, hsv.v, hsv.a), [hsv]);

    const updateHsv = useCallback(
      (next: { h?: number; s?: number; v?: number; a?: number }) => {
        const merged = { ...hsv, ...next };
        setHsv(merged);
        const p = buildParsed(merged.h, merged.s, merged.v, merged.a);
        const formatted = formatValueByFormat(p, currentFormat);
        lastEmittedRef.current = formatted;
        if (!isControlled) setInternalValue(formatted);
        onValueChange?.(formatted, p);
      },
      [hsv, currentFormat, isControlled, onValueChange],
    );

    const handleFormatChange = useCallback(
      (f: ColorFormat) => {
        if (!isFormatControlled) setInternalFormat(f);
        onFormatChange?.(f);
        const formatted = formatValueByFormat(parsed, f);
        lastEmittedRef.current = formatted;
        if (!isControlled) setInternalValue(formatted);
        onValueChange?.(formatted, parsed);
      },
      [isFormatControlled, isControlled, onFormatChange, onValueChange, parsed],
    );

    const handleHexCommit = useCallback(
      (input: string) => {
        const p = parseColor(input);
        if (!p) return;
        const newHsv = rgbToHsv(p.r, p.g, p.b);
        const merged = {
          h: newHsv.s === 0 ? hsv.h : newHsv.h,
          s: newHsv.s,
          v: newHsv.v,
          a: p.a,
        };
        setHsv(merged);
        const next = buildParsed(merged.h, merged.s, merged.v, merged.a);
        const formatted = formatValueByFormat(next, currentFormat);
        lastEmittedRef.current = formatted;
        if (!isControlled) setInternalValue(formatted);
        onValueChange?.(formatted, next);
      },
      [hsv.h, currentFormat, isControlled, onValueChange],
    );

    const solidHueRgb = useMemo(() => hsvToRgb(hsv.h, hsv.s, hsv.v), [hsv.h, hsv.s, hsv.v]);
    const solidColorString = `rgb(${Math.round(solidHueRgb.r)}, ${Math.round(solidHueRgb.g)}, ${Math.round(solidHueRgb.b)})`;
    const hueColor = `hsl(${hsv.h}, 100%, 50%)`;

    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-3', className)}
        style={{ width: PANEL_WIDTH }}
        {...props}
      >
        <SaturationSquare h={hsv.h} s={hsv.s} v={hsv.v} onChange={(s, v) => updateHsv({ s, v })} />

        <div className="flex flex-col gap-2">
          <ChannelBar
            value={hsv.h}
            min={0}
            max={360}
            step={1}
            onChange={(h) => updateHsv({ h })}
            ariaLabel="Hue"
            thumbColor={hueColor}
            trackStyle={{
              background:
                'linear-gradient(to right, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%))',
            }}
          />
          <ChannelBar
            value={Math.round(hsv.a * 100)}
            min={0}
            max={100}
            step={1}
            onChange={(a) => updateHsv({ a: a / 100 })}
            ariaLabel="Alpha"
            thumbColor={solidColorString}
            trackStyle={{
              backgroundImage: `linear-gradient(to right, transparent, ${solidColorString}), conic-gradient(#bbb 0 25%, #fff 0 50%, #bbb 0 75%, #fff 0)`,
              backgroundSize: '100% 100%, 8px 8px',
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <FormatSelect
            value={currentFormat}
            options={allowedFormats}
            onChange={handleFormatChange}
          />
          {!hideEyedropper && <EyeDropperButton onPick={handleHexCommit} />}
        </div>

        <ColorInputsRow
          parsed={parsed}
          format={currentFormat}
          onChannelChange={(channel, raw) => {
            const p = { ...parsed };
            switch (channel) {
              case 'hex':
                handleHexCommit(raw);
                return;
              case 'r':
              case 'g':
              case 'b': {
                const r = channel === 'r' ? Number(raw) : p.r;
                const g = channel === 'g' ? Number(raw) : p.g;
                const b = channel === 'b' ? Number(raw) : p.b;
                const hsvVal = rgbToHsv(r, g, b);
                updateHsv({
                  h: hsvVal.s === 0 ? hsv.h : hsvVal.h,
                  s: hsvVal.s,
                  v: hsvVal.v,
                });
                return;
              }
              case 'hSL':
              case 'sSL':
              case 'lSL': {
                const hsl = rgbToHsl(p.r, p.g, p.b);
                const h2 = channel === 'hSL' ? Number(raw) : hsl.h;
                const s2 = channel === 'sSL' ? Number(raw) / 100 : hsl.s;
                const l2 = channel === 'lSL' ? Number(raw) / 100 : hsl.l;
                const rgb = hslToRgb(h2, clamp01(s2), clamp01(l2));
                const hsvVal = rgbToHsv(rgb.r, rgb.g, rgb.b);
                updateHsv({
                  h: hsvVal.s === 0 ? h2 : hsvVal.h,
                  s: hsvVal.s,
                  v: hsvVal.v,
                });
                return;
              }
              case 'L':
              case 'C':
              case 'H': {
                const cur = rgbToOklch(p.r, p.g, p.b);
                const L = channel === 'L' ? Number(raw) / 100 : cur.L;
                const C = channel === 'C' ? Number(raw) : cur.C;
                const H = channel === 'H' ? Number(raw) : cur.H;
                const rgb = oklchToRgb(clamp01(L), Math.max(0, C), H);
                const hsvVal = rgbToHsv(rgb.r, rgb.g, rgb.b);
                updateHsv({
                  h: hsvVal.s === 0 ? hsv.h : hsvVal.h,
                  s: hsvVal.s,
                  v: hsvVal.v,
                });
                return;
              }
              case 'alphaPercent':
                updateHsv({ a: clamp01(Number(raw) / 100) });
                return;
            }
          }}
        />

        {swatches && swatches.length > 0 && (
          <SwatchStrip swatches={swatches} current={parsed.hex} onPick={handleHexCommit} />
        )}
      </div>
    );
  },
);

ColorPicker.displayName = 'ColorPicker';

export { ColorPicker, ColorSwatch, parseColor, COLOR_FORMATS };
export type { ColorPickerProps, ColorFormat, ParsedColor };
